import type { Lot, ProposalOrder } from '@moment/horizon-data-access/proposal'
import type { Holding } from '@/types/api/risk-factor'

import { OrderSide } from '@/types/enums/order'

// Aggregates lots by ISIN for a single sleeve. No cash, no orders.
export function computeHoldings(lots: Lot[], sleeveId: string): Holding[] {
  const sleeveLots = sleeveId
    ? lots.filter((lot) => lot.sleeve_id === sleeveId)
    : lots

  return aggregateLotsByIsin(sleeveLots)
}

interface IsinAccumulator {
  quantity: number
  marketValue?: number
  sleeveWeight?: number
  accountWeight?: number
}

function addWeight(
  current: number | undefined,
  lot: number | null | undefined,
) {
  return lot == null ? current : (current ?? 0) + lot
}

// Aggregates lots by ISIN for whatever scope the caller passes (a sleeve's
// lots, or the account's full lot list).
export function aggregateLotsByIsin(lots: Lot[]): Holding[] {
  const byIsin = new Map<string, IsinAccumulator>()
  for (const lot of lots) {
    const acc = byIsin.get(lot.instrument.isin) ?? { quantity: 0 }
    acc.quantity += lot.open_quantity
    if (lot.open_market_value != null) {
      acc.marketValue = (acc.marketValue ?? 0) + lot.open_market_value
    }
    acc.sleeveWeight = addWeight(acc.sleeveWeight, lot.sleeve_weight)
    acc.accountWeight = addWeight(acc.accountWeight, lot.account_weight)
    byIsin.set(lot.instrument.isin, acc)
  }

  return Array.from(byIsin.entries()).map(
    ([
      instrument_id,
      { quantity, marketValue, sleeveWeight, accountWeight },
    ]) => ({
      instrument_id,
      quantity,
      market_value: marketValue,
      sleeve_weight: sleeveWeight,
      account_weight: accountWeight,
    }),
  )
}

interface ComputeHoldingsAfterInput {
  beforeLots: Lot[]
  afterLots: Lot[]
  orders: ProposalOrder[]
  accountId: string
  // When omitted, lots and buy allocations are scoped to the account only.
  sleeveId?: string
}

// Aggregates post-trade lots by ISIN and attaches quantity-weighted limit
// prices for net-new ISINs from buy orders. Cash is not a holding — read it
// from the `sleeveCash` store selector. Pure port of the previous
// `extractHoldingsFromProposal` body, decoupled from `DetailedProposal` so it
// can be driven by store state pieces directly.
export function computeHoldingsAfter({
  beforeLots,
  afterLots,
  orders,
  accountId,
  sleeveId,
}: ComputeHoldingsAfterInput): Holding[] {
  const filteredBefore = sleeveId
    ? beforeLots.filter((lot) => lot.sleeve_id === sleeveId)
    : beforeLots
  const beforeIsins = new Set(filteredBefore.map((lot) => lot.instrument.isin))

  const filteredAfter = sleeveId
    ? afterLots.filter((lot) => lot.sleeve_id === sleeveId)
    : afterLots

  const buyLimits = aggregateBuyOrderLimitPrices(orders, accountId, sleeveId)

  const holdings: Holding[] = aggregateLotsByIsin(filteredAfter).map(
    (holding) => {
      const isNetNew = !beforeIsins.has(holding.instrument_id)
      const limits = isNetNew ? buyLimits.get(holding.instrument_id) : undefined
      if (limits) {
        return {
          ...holding,
          limit_price: limits.limit_price,
          limit_ytw: limits.limit_ytw,
        }
      }
      return holding
    },
  )

  return holdings
}

function aggregateBuyOrderLimitPrices(
  orders: ProposalOrder[],
  accountId: string,
  sleeveId?: string,
): Map<string, Pick<Holding, 'limit_price' | 'limit_ytw'>> {
  const aggregates = new Map<
    string,
    { weightedPrice: number; weightedYtw: number; totalQty: number }
  >()

  for (const order of orders) {
    if (order.side !== OrderSide.Buy) continue

    const orderQty = order.allocations
      .filter(
        (a) =>
          a.moment_account_id === accountId &&
          (!sleeveId || a.sleeve_id === sleeveId),
      )
      .reduce((sum, a) => sum + a.quantity, 0)
    if (orderQty === 0) continue

    const agg = aggregates.get(order.instrument_id) ?? {
      weightedPrice: 0,
      weightedYtw: 0,
      totalQty: 0,
    }
    agg.weightedPrice += order.limit_price * orderQty
    agg.weightedYtw += (order.limit_ytw ?? 0) * orderQty
    agg.totalQty += orderQty
    aggregates.set(order.instrument_id, agg)
  }

  const result = new Map<string, Pick<Holding, 'limit_price' | 'limit_ytw'>>()
  for (const [isin, agg] of aggregates) {
    result.set(isin, {
      limit_price: agg.weightedPrice / agg.totalQty,
      limit_ytw: agg.weightedYtw / agg.totalQty,
    })
  }
  return result
}
